import PlatformSwitcher from '@/components/config_components/PlatformSwitcher/PlatformSwitcher.vue';
import axios from 'axios';
import {
  UPDATE_USER,
  VIEW_USER_PROFILE,
  get_request_config,
} from '@/utils/system_constants/constant.js';
import {
  extract_error_message,
  parse_response_data,
} from '@/utils/api_response_helpers';
import alert_mixin from "@/utils/mixins/alert_mixin";
import {
  LAUNCH_PLATFORM_URL,
  get_headers,
} from '@/utils/system_constants/constant';

const NAV_HISTORY_KEY = "config_nav_history";

const ROUTE_LABELS = {
  GlobalOverview: "Overview",
  ProjectManagement: "Projects",
  UserManagement: "Users",
  RolesManagement: "Roles",
  EntityConfigs: "Entity Configs",
  EntityConfigDetail: "Entity Details",
  EntitySubsystemBindings: "Subsystem Bindings",
  CreateForm: "Create Form",
  CreateSections: "Create Sections",
  ManageQuestions: "Manage Questions",
  PreviewForm: "Preview Form",
  ViewTemplate: "View Templates",
  UploadTemplate: "Upload Template",
  EsigningSetup: "E-signing Setup",
  TemplateFormManagement: "Template Form Management",
  TemplateQuestionMapping: "Template Question Mapping",
  ConfigureDocusealTemplate: "Configure Docuseal Template",
  EsigningWorkflows: "eSigning Workflows",
  CreateWorkflow: "Create Workflow",
  ConfigureWorkflow: "Configure Workflow",
  ManageStatus: "Manage Status",
  CreateRiskFramework: "Create Risk Framework",
  ComplianceManagement: "Compliance Management",
  ManageDocuments: "Documents",
  ManageSubEntities: "Manage Sub Entities",
  SubEntityRequirements: "Sub Entity Requirements",
  VerificationServices: "Verification Services",
  ConfigureVerificationServices: "Configure Verification Services",
  ConfigureVerificationForm: "Configure Verification Form",
  VerificationForm: "Verification Forms",
  FormQuestionMapping: "Form Question Mapping",
  ManualVerification: "Manual Verification",
  ManualVerificationBinding: "Manual Verification Binding",
  OrganizationDashboard: "Organization Dashboard",
};

const ROUTE_LABEL_MAP = new Map(Object.entries(ROUTE_LABELS));
const ROUTE_QUERY_LABEL_RESOLVERS = [
  (route) => route.query?.form_name,
  (route) => route.query?.entity_type_name,
  (route) => route.query?.workflow_name,
  (route) => route.query?.template_name,
  (route) => route.query?.project_name,
];
const MAX_TRAIL_LENGTH = 6;

function prettifyRouteName(routeName) {
  return routeName
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .trim();
}

function parseTrailJson(rawTrail) {
  try {
    return JSON.parse(rawTrail || "[]");
  } catch (_error) {
    return [];
  }
}

function readStoredTrail() {
  const storedTrail = parseTrailJson(sessionStorage.getItem(NAV_HISTORY_KEY));
  return Array.isArray(storedTrail) ? storedTrail : [];
}

function saveStoredTrail(trail) {
  try {
    sessionStorage.setItem(NAV_HISTORY_KEY, JSON.stringify(trail));
  } catch (_error) {
    // Session storage can be unavailable in some privacy modes.
  }
}

export default {
  name: "ConfigNavbar",
  components: { PlatformSwitcher },
  mixins: [alert_mixin],
  props: {
    hide_hamburger: {
      type: Boolean,
      default: false,
    },
    show_mobile_drawer: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    navTrail: [],
    profile_loading: false,
    profile_saving: false,
    profile_error: '',
    profile_success: '',
    profile_form: {
      user_id: null,
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
      sms_number: '',
      whatsapp_number: '',
      role: '',
    },
  }),
  computed: {
    breadcrumbItems() {
      const trail = this.navTrail.slice(-MAX_TRAIL_LENGTH);
      const trailItems = trail.map((entry, index) => ({
        label: entry.label,
        to: entry.to,
        current: index === trail.length - 1,
      }));

      return [{ label: "System", to: this.systemHomeRoute }, ...trailItems];
    },
    systemHomeRoute() {
      return this.$store.getters.isOrganizationAdmin === true
        ? { name: "OrganizationDashboard" }
        : { name: "GlobalOverview" };
    },
    currentRouteLabel() {
      return this.resolveRouteLabel(this.$route);
    },
    role() {
      return (
        this.$store.getters.currentRole
        || sessionStorage.getItem("current_role")
        || sessionStorage.getItem("user_role")
        || ""
      );
    },
    is_admin_role() {
      const normalizedRole = String(this.role || "").trim().toLowerCase();
      return normalizedRole.includes("admin") || normalizedRole.includes("administrator");
    },
    organizationName() {
      if (this.$store.getters.isOrganizationAdmin !== true) {
        return "";
      }
      return this.$store.getters.organization?.name || "";
    },
    isOrganizationAdmin() {
      return this.$store.getters.isOrganizationAdmin === true;
    },
  },
  mounted() {
    this.syncTrailWithRoute(this.$route, true);
  },
  watch: {
    $route(to) {
      this.syncTrailWithRoute(to, false);
    },
  },
  methods: {
    resolveEntityConfigLabel(route) {
      const projectName = String(route.query?.project_name || "").trim();
      return projectName ? `Entity Configurations for ${projectName}` : null;
    },
    resolveQueryLabel(route) {
      const label = ROUTE_QUERY_LABEL_RESOLVERS
        .map((resolveLabel) => resolveLabel(route))
        .find((value) => String(value || "").trim());

      return label ? String(label).trim() : null;
    },
    resolveNamedRouteLabel(route) {
      if (route.name && ROUTE_LABEL_MAP.has(route.name)) {
        return ROUTE_LABEL_MAP.get(route.name);
      }

      return typeof route.name === "string" ? prettifyRouteName(route.name) : null;
    },
    resolveRouteLabel(route) {
      if (!route) return "Unknown";

      return (route.name === "EntityConfigs" && this.resolveEntityConfigLabel(route))
        || this.resolveQueryLabel(route)
        || this.resolveNamedRouteLabel(route)
        || "Current Page";
    },
    routeToTrailEntry(route) {
      const to = route.name
        ? {
            name: route.name,
            params: { ...route.params },
            query: { ...route.query },
          }
        : {
            path: route.path,
            query: { ...route.query },
          };

      const trailEntry = {};
      trailEntry.label = this.resolveRouteLabel(route);
      trailEntry.to = to;
      trailEntry.fullPath = route.fullPath;
      return trailEntry;
    },
    getStoredTrail() {
      return readStoredTrail();
    },
    mergeTrailEntry(storedTrail, entry) {
      const existingIndex = storedTrail.findIndex((item) => item.fullPath === entry.fullPath);
      return existingIndex >= 0
        ? storedTrail.slice(0, existingIndex + 1)
        : [...storedTrail, entry];
    },
    persistTrail(trail) {
      saveStoredTrail(trail);
    },
    syncTrailWithRoute(route, replaceAll = false) {
      if (!route?.name && !route?.path) return;

      const entry = this.routeToTrailEntry(route);
      const storedTrail = replaceAll ? [] : this.getStoredTrail();
      const trimmedTrail = this.mergeTrailEntry(storedTrail, entry).slice(-MAX_TRAIL_LENGTH);
      this.navTrail = trimmedTrail;
      this.persistTrail(trimmedTrail);
    },
    isClickable(item) {
      return Boolean(item.to?.name || item.to?.path);
    },
    parse_profile(profile) {
      const user = profile?.user || profile || {};
      const role = user.role;

      const parsedProfile = {};
      parsedProfile.user_id = profile?.user_id || user.id || this.$store.getters.currentUserId;
      parsedProfile.first_name = user.first_name || '';
      parsedProfile.last_name = user.last_name || '';
      parsedProfile.email = user.email || '';
      parsedProfile.phone_number = profile?.phone_number || '';
      parsedProfile.sms_number = profile?.sms_number || '';
      parsedProfile.whatsapp_number = profile?.whatsapp_number || '';
      parsedProfile.role = typeof role === 'object' ? role.role : role || '';
      return parsedProfile;
    },
    async open_profile_modal() {
      this.profile_error = '';
      this.profile_success = '';
      this.profile_loading = true;

      try {
        const user_id = this.$store.getters.currentUserId;
        const response = await axios.post(
          VIEW_USER_PROFILE,
          { user_id },
          get_request_config()
        );
        const response_data = parse_response_data(response);
        const profile = Array.isArray(response_data?.data)
          ? response_data.data[0]
          : response_data?.data;

        if (response_data?.status !== 'success' || !profile) {
          this.show_error(response_data?.message || 'Could not load your profile.');
        }

        this.profile_form = this.parse_profile(profile);
      } catch (error) {
        this.profile_error = extract_error_message(error, 'Could not load your profile.');
      } finally {
        this.profile_loading = false;
      }
    },
    async save_profile() {
      this.profile_error = '';
      this.profile_success = '';
      this.profile_saving = true;

      try {
        const response = await axios.post(
          UPDATE_USER,
          {
            user_id: this.profile_form.user_id || this.$store.getters.currentUserId,
            first_name: this.profile_form.first_name.trim(),
            last_name: this.profile_form.last_name.trim(),
            email: this.profile_form.email.trim(),
            phone_number: this.profile_form.phone_number.trim(),
            sms_number: this.profile_form.sms_number.trim(),
            whatsapp_number: this.profile_form.whatsapp_number.trim(),
          },
          get_request_config()
        );
        const response_data = parse_response_data(response);

        if (response_data?.status && response_data.status !== 'success') {
          this.show_error(response_data.message || 'Could not update your profile.');
        }

        this.profile_form = this.parse_profile({
          user_id: this.profile_form.user_id,
          user: this.profile_form,
          phone_number: this.profile_form.phone_number,
          sms_number: this.profile_form.sms_number,
          whatsapp_number: this.profile_form.whatsapp_number,
        });
        this.profile_error = '';
        this.profile_success = 'Your profile information was updated successfully.';
      } catch (error) {
        this.profile_error = extract_error_message(error, 'Could not update your profile.');
      } finally {
        this.profile_saving = false;
      }
    },
    async goToOnboardingSystem() {
      if (!this.is_admin_role) return;

      await this.launchPlatform("x-verdict");
    },
    async launchPlatform(platform) {
      const externalUserId = sessionStorage.getItem("user_id")
        || sessionStorage.getItem("current_user_id");

      try {
        const response = await axios.post(
          LAUNCH_PLATFORM_URL,
          { platform, external_user_id: externalUserId },
          { headers: get_headers() }
        );
        const responseData = response.data || {};

        if (responseData.redirect_url) {
          window.location.href = responseData.redirect_url;
          return;
        }

        this.show_error("Failed to launch platform: No redirect URL provided");
      } catch (error) {
        if (error.response?.status === 403) {
          this.show_error("Access denied to this platform");
        } else if (error.response?.status === 404) {
          this.show_error("Platform not found or unavailable");
        } else {
          this.show_error("Failed to launch platform");
        }
      }
    },
  },
};
